'use client'

import { contactFormAction } from '@/lib/actions'
import { formSchema } from '@/lib/contact'
import { BlokProps } from '@/lib/types'
import { ContactFormStoryblok } from '@/lib/types/component-types-sb'
import { zodResolver } from '@hookform/resolvers/zod'
import { storyblokEditable } from '@storyblok/react'
import { CheckCircle2, HelpCircle, Server, Shield } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import Link from 'next/link'
import { useSearchParams } from 'next/navigation'
import { Suspense, useActionState, useEffect, useMemo } from 'react'
import { useForm, useFormContext, useWatch } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { Container } from '../shared/container'
import { DynamicForm } from '../shared/dynamic-form'
import { SectionHeader } from '../shared/section-header'
import { Alert, AlertDescription, AlertTitle } from '../ui/alert'
import { Checkbox } from '../ui/checkbox'
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../ui/form'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'

function ConditionalUseCaseInputs() {
  const form = useFormContext()
  const useCase = useWatch({ control: form.control, name: 'useCase' })

  return (
    <AnimatePresence>
      {Array.isArray(useCase) && useCase.length > 0 && (
        <motion.div
          initial={{ opacity: 0, height: 0, overflow: 'hidden' }}
          animate={{ opacity: 1, height: 'auto', overflow: 'visible' }}
          exit={{ opacity: 0, height: 0, overflow: 'hidden' }}
          transition={{ duration: 0.3 }}
        >
          <FormField
            control={form.control}
            name="orderVolume"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Order volume</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  value={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select order volume per month" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="not-live">Not live yet.</SelectItem>
                    <SelectItem value="1-100">0-500 / month</SelectItem>
                    <SelectItem value="101-500">500-2,500 / month</SelectItem>
                    <SelectItem value="501-1000">2,500-10,000 / month</SelectItem>
                    <SelectItem value="10000-100000">10,000-100,000 / month</SelectItem>
                    <SelectItem value="100000-1000000">100,000-1,000,000 / month</SelectItem>
                  </SelectContent>
                </Select>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="companySize"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Company size</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  value={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select company size" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="small">1-10 employees</SelectItem>
                    <SelectItem value="medium">11-100 employees</SelectItem>
                    <SelectItem value="large">101-500 employees</SelectItem>
                    <SelectItem value="enterprise">501+ employees</SelectItem>
                  </SelectContent>
                </Select>
              </FormItem>
            )}
          />
        </motion.div>
      )}
    </AnimatePresence>
  )
}

function InterestedInField() {
  const form = useFormContext()

  const options = [
    {
      label: 'Managed Hosting',
      description: 'Enterprise-grade hosting with Kubernetes',
      value: 'managed-hosting',
      icon: Server,
    },
    {
      label: 'Support Services',
      description: 'Consulting, development and training.',
      value: 'support-services',
      icon: HelpCircle,
    },
    {
      label: 'Enterprise Edition',
      description: 'Commercial license and SLA-backed priority support.',
      value: 'enterprise-edition',
      icon: Shield,
    },
  ]

  return (
    <FormField
      control={form.control}
      name="interestedIn"
      render={({ field }) => (
        <FormItem>
          <FormLabel>Interested in</FormLabel>
          <div className="flex flex-col gap-2">
            {options.map((option) => {
              const isSelected = field.value.includes(option.value)

              const toggle = (isSelected: boolean) => {
                const newValue = isSelected
                  ? [...field.value, option.value]
                  : field.value.filter((v) => v !== option.value)

                field.onChange(newValue)
              }

              return (
                <div
                  key={field.name + option.value}
                  className="bg-sidebar border-border flex items-center justify-start gap-3 rounded-lg border px-3 py-2"
                >
                  <Checkbox
                    onCheckedChange={toggle}
                    className="bg-background"
                    checked={isSelected}
                  />

                  <option.icon className="text-brand hidden h-6 w-6 lg:block" />
                  <div className="flex flex-col">
                    <span className="text-accent-foreground text-sm font-medium lg:text-base">
                      {option.label}
                    </span>
                    <span className="text-muted-foreground text-xs lg:text-sm">
                      {option.description}
                    </span>
                  </div>
                </div>
              )
            })}
          </div>
          <FormDescription />
          <FormMessage />
        </FormItem>
      )}
    />
  )
}

function ContactForm() {
  const searchParams = useSearchParams()

  // Parse interestedIn from search params
  const interestedInParam = searchParams.get('interestedIn')
  const initialInterestedIn = useMemo(() => {
    if (!interestedInParam) return []

    const validValues = ['managed-hosting', 'support-services', 'enterprise-edition']
    const paramValues = interestedInParam.split(',').map((v) => v.trim())

    return paramValues.filter((value) => validValues.includes(value))
  }, [interestedInParam])

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
      acceptTerms: false,
      interestedIn: initialInterestedIn,
      useCase: [],
      useCaseDescription: '',
      newsletterSubscribe: false,
      company: '',
      name: '',
    },
  })
  const [state, action] = useActionState(contactFormAction, {
    success: false,
    message: '',
  })

  useEffect(() => {
    if (state.success && state.message != '') {
      form.reset()
      toast.success(state.message)
    }
  }, [state.success])

  return (
    <div>
      {!state.success && state.message != '' && (
        <Alert variant="destructive">
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>
            {state.message}

            {state.errors && (
              <ul>
                {Object.keys(state.errors).map((key) => (
                  <li key={key}>{state.errors[key]}</li>
                ))}
              </ul>
            )}
          </AlertDescription>
        </Alert>
      )}
      <DynamicForm
        data-testid="contact-form"
        action={action}
        form={form}
        fields={[
          {
            type: 'custom',
            name: 'interestedIn',
            component: InterestedInField,
          },
          {
            type: 'text',
            name: 'company',
            label: 'Company',
          },
          {
            type: 'text',
            name: 'name',
            label: 'Name',
          },
          {
            type: 'email',
            name: 'email',
            label: 'Email',
          },
          {
            type: 'multiselect',
            name: 'useCase',
            label: 'Use case',
            placeholder: 'Select one or multiple use cases',
            options: [
              {
                label: 'Direct to Consumer (D2C)',
                value: 'd2c',
              },
              {
                label: 'Business to Business (B2B)',
                value: 'b2b',
              },
              {
                label: 'Multi-vendor (Marketplace)',
                value: 'marketplace',
              },
              {
                label: 'Custom Software (SaaS, ...)',
                value: 'custom-software',
              },
              {
                label: 'Subscriptions',
                value: 'subscriptions',
              },
              {
                label: 'Other',
                value: 'other',
              },
            ],
          },
          {
            type: 'custom',
            name: 'useCaseInformation',
            component: ConditionalUseCaseInputs,
          },
          {
            type: 'textarea',
            name: 'useCaseDescription',
            label: 'Use case description',
            placeholder: 'Describe your use case with Vendure',
          },
          {
            type: 'checkbox',
            name: 'newsletterSubscribe',
            label: 'Subscribe to our monthly newsletter',
          },
          {
            type: 'checkbox',
            name: 'acceptTerms',
            label: (
              <p>
                I have read the{' '}
                <Link
                  href="/privacy-policy"
                  target="_blank"
                  className="underline"
                >
                  privacy policy
                </Link>{' '}
                and accept the further processing of my personal data.
              </p>
            ),
          },
        ]}
        submitButton={{
          label: 'Send now',
        }}
      />
    </div>
  )
}

export function ContactFormSection({ blok }: BlokProps<ContactFormStoryblok>) {
  const options = useMemo(() => {
    return blok.options?.split('\n') ?? []
  }, [blok.options])

  return (
    <section
      {...storyblokEditable(blok)}
      className="border-border border-b"
    >
      <Container className="border-border border-x">
        <div className="grid grid-cols-1 lg:grid-cols-2">
          <div className="space-y-6 p-6 lg:px-12 lg:py-16">
            <SectionHeader
              titleTag="h1"
              layout="stacked"
              preTitle={blok.descriptorTitle}
              title={blok.title}
              content={blok.content}
              icon={blok.descriptorIcon}
            />
            <div className="text-accent-foreground flex flex-col gap-2 font-medium lg:text-lg">
              {options.map((option) => (
                <div
                  key={option}
                  className="flex items-center justify-start gap-2"
                >
                  <CheckCircle2 className="text-muted-foreground fill-muted" />
                  {option}
                </div>
              ))}
            </div>
          </div>
          <div className="px-6 pb-12 lg:px-12 lg:py-16">
            <Suspense fallback={<div>Loading form...</div>}>
              <ContactForm />
            </Suspense>
          </div>
        </div>
      </Container>
    </section>
  )
}
